import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface OnboardingBackfillState {
  /** Session ID of the backfill started from onboarding (for matching errors from extendedBackfillMetrics) */
  sessionId: string | null;
  /** Org UUID when backfill was started (needed to call setInternalOnboardingTeamInsights on completion) */
  orgUuid: string | null;
  /** Last error from onboarding backfill, if any */
  error: string | null;
  /** Timestamp when onboarding backfill was started */
  startedAt: number | null;
  /** Final result from backfill when completed successfully */
  result: unknown;
  /** Timestamp when onboarding backfill completed successfully */
  completedAt: number | null;
  /** Whether backfill completed successfully */
  completed: boolean;
  /** Whether the internal_onboarding_team_insights flag was set to 1 */
  flagSet: boolean;
  setSessionId: (sessionId: string | null) => void;
  setError: (error: string | null) => void;
  setStartedAt: (startedAt: number | null) => void;
  /** Mark that onboarding backfill was started; call before startBackfill() */
  markStarted: (sessionId: string, orgUuid: string) => void;
  /** Mark completed successfully and store final result */
  markCompleted: (result: unknown) => void;
  /** Mark that the onboarding flag was verified as set */
  markFlagSet: () => void;
  /** Clear error (e.g. after user dismisses) */
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  sessionId: null as string | null,
  orgUuid: null as string | null,
  error: null as string | null,
  startedAt: null as number | null,
  result: null as unknown,
  completedAt: null as number | null,
  completed: false,
  flagSet: false,
};

export const useOnboardingBackfillStore = create<OnboardingBackfillState>()(
  persist(
    (set) => ({
      ...initialState,

      setSessionId: (sessionId) => set({ sessionId }),
      setError: (error) => set({ error }),
      setStartedAt: (startedAt) => set({ startedAt }),

      markStarted: (sessionId, orgUuid) =>
        set({
          sessionId,
          orgUuid,
          error: null,
          startedAt: Date.now(),
          result: null,
          completedAt: null,
          completed: false,
          flagSet: false,
        }),

      markCompleted: (result) =>
        set({
          result,
          completedAt: Date.now(),
          completed: true,
        }),

      markFlagSet: () => set({ flagSet: true }),

      clearError: () => set({ error: null }),
      reset: () => set(initialState),
    }),
    {
      name: 'onboarding-backfill-storage',
      partialize: (state) => ({
        sessionId: state.sessionId,
        orgUuid: state.orgUuid,
        error: state.error,
        startedAt: state.startedAt,
        result: state.result,
        completedAt: state.completedAt,
        completed: state.completed,
        flagSet: state.flagSet,
      }),
    }
  )
);
